import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import DomPurify from 'dompurify';
import { ServiceCard } from '../../commons/ServiceCard';
import { ButtonClassNames, GlobalRouteNames, ServiceCardIconType } from '../../../models/Common';
import { TextButton } from '../../commons/buttons/TextButton';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { CustomTooltipInfo } from '../../commons/CustomTooltipInfo';
import { handleServicePaymentAnalytics } from '../../../utils';
import { DataLayerButtonsTypes } from '../../../types/dataLayerTypes';
import { ReactComponent as InfoIcon } from '../../../assets/images/InfoOutlined.svg';

interface ChangeServiceCardProps {
  name: string;
  price: number | string;
  description: string;
  isDaily: boolean;
  iconType: ServiceCardIconType;
  isAvailableForConnection: boolean;
  isCurrent: boolean;
  handleChange: (id: string | number, isDaily: boolean, isCurrent: boolean) => void;
  serviceId: string | number;
  handleCallback: (serviceName: string, serviceAnalyticsName: string) => void;
  isPrepaid: boolean;
  serviceAnalyticsName: string | null;
  canChange: boolean;
  canActivate: boolean;
  isCashback: boolean;
}

export const ChangeServiceCard: FC<ChangeServiceCardProps> = ({
  isAvailableForConnection,
  iconType,
  name,
  price,
  description,
  isDaily,
  isCurrent,
  handleChange,
  serviceId,
  handleCallback,
  isPrepaid,
  serviceAnalyticsName,
  canChange,
  canActivate,
  isCashback,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const handleCallMeBack = () => {
    handleCallback(name, serviceAnalyticsName ?? name);
  };

  const handleChangeService = (currentService: string, buttonName: string) => {
    handleChange(serviceId, isDaily, isCurrent);
    handleServicePaymentAnalytics(currentService, buttonName);
  };

  const sanitizer = DomPurify.sanitize;

  const handleGoToPaymentHistory = () => {
    history.push({ pathname: GlobalRouteNames.PaymentHistory });
  };

  return (
    <ServiceCard iconType={iconType}>
      <div className="info">
        <div className="info__name">
          <h4>{name}</h4>
        </div>
        {(isCurrent || isPrepaid) && (
          <div className="info__status">
            {isCurrent && <p className="info__status_active">{t('common.connected')}</p>}
            {isPrepaid && (
              <TextButton
                isArrow
                label={t('services.prepaid')}
                className="primary"
                handleClick={handleGoToPaymentHistory}
              />
            )}
          </div>
        )}
        <p className="info__description" dangerouslySetInnerHTML={{ __html: sanitizer(description) }} />
        <h5 className="medium info__price">
          {price} {isDaily ? t('services.uahPerDay') : t('services.uahPerMonth')}
          {isCashback && !isDaily ? (
            <CustomTooltipInfo
              tooltipText=""
              element={
                <>
                  <div style={{ maxWidth: '250px' }}>{t('changeService.cashbackTerms')}</div>
                </>
              }
            >
              <div className="info__price__tooltip">
                {t('buttons.cashBack')} <InfoIcon />
              </div>
            </CustomTooltipInfo>
          ) : null}
        </h5>
      </div>
      <div className="btn">
        {isAvailableForConnection ? (
          <>
            {isCurrent ? (
              <CustomMUIButton
                label={t('buttons.proceedToCheckout')}
                handleClick={() => handleChangeService(serviceAnalyticsName ?? name, DataLayerButtonsTypes.CONNECT)}
                className={ButtonClassNames.PrimaryFulfilled}
                isDisabled={!canActivate}
              />
            ) : (
              <CustomMUIButton
                label={canChange ? t('buttons.connect') : t('buttons.order')}
                handleClick={
                  canChange
                    ? () => handleChangeService(serviceAnalyticsName ?? name, DataLayerButtonsTypes.CONNECT)
                    : handleCallMeBack
                }
                className={canChange ? ButtonClassNames.SecondaryOutlined : ButtonClassNames.YellowFulfilled}
              />
            )}
          </>
        ) : (
          <CustomMUIButton
            label={t('buttons.order')}
            handleClick={handleCallMeBack}
            className={ButtonClassNames.YellowFulfilled}
          />
        )}
      </div>
    </ServiceCard>
  );
};
